// The Daily Challenge leaderboard lifecycle: keep the finished daily around,
// auto-submit it to the Worker, and paint the leaderboard panel. Pulled out of
// App because it's a cohesive unit with its own session-scoped state (the
// "already tried to submit today" flag) and its own network coupling — none of
// which the screen renderers need to know about.
import type { GameState, SeasonState } from "../types";
import {
  fetchLeaderboard,
  getClientId,
  isOptedOut,
  leaderboardNoticeSeen,
  markLeaderboardNoticeSeen,
  submitScore,
} from "../state/leaderboard";
import { esc } from "./helpers";
import { openLeaderboardNoticeModal } from "./modals";
import { t } from "./i18n";

// What the controller needs from App: the live game state (via a getter so it
// survives a reset that reassigns state) and a way to persist it.
export interface DailyDeps {
  getState(): GameState;
  persist(): void;
}

export class DailyController {
  // The daily we've already attempted to submit this session, so a result
  // re-render (tab switch) doesn't re-fire the network call. Persistent
  // double-submit protection lives in state.lastSubmittedDay.
  private submitTriedDay: string | null = null;

  constructor(private readonly deps: DailyDeps) {}

  // Keep the finished Daily Challenge season so the menu can show the result and
  // a retry jumps straight to it (rather than replaying the same seed).
  recordResult(s: SeasonState): void {
    if (s.standings) this.deps.getState().dailySeason = s;
  }

  // Auto-submit a finished daily to the leaderboard (the player chose this over
  // an explicit button). Idempotent: at most one network attempt per session
  // (submitTriedDay) and never re-posts a day already accepted
  // (state.lastSubmittedDay). Everything is best-effort — failures never disrupt
  // the game, and the player can silence future posts via the one-time notice.
  ensureSubmitted(s: SeasonState): void {
    const state = this.deps.getState();
    if (!s.daily || !s.standings || !s.day) return;
    if (isOptedOut()) return;
    if (state.lastSubmittedDay === s.day) return;
    if (this.submitTriedDay === s.day) return;
    this.submitTriedDay = s.day;
    void submitScore({
      day: s.day,
      clientId: getClientId(),
      name: s.teamName,
      chaos: s.chaos,
      decisionLog: s.decisionLog ?? [],
    }).then((ok) => {
      const st = this.deps.getState();
      if (ok) {
        st.lastSubmittedDay = s.day;
        this.deps.persist();
        // Refresh the panel in place if the player is looking at it.
        if (st.phase === "result" && st.season === s) {
          void this.renderLeaderboard(s);
        }
      } else {
        // Allow a retry on the next result view (e.g. came back online).
        this.submitTriedDay = null;
      }
      // First successful-or-not post of all time: explain it once.
      if (ok && !leaderboardNoticeSeen()) {
        markLeaderboardNoticeSeen();
        openLeaderboardNoticeModal();
      }
    });
  }

  // Fill the daily result's leaderboard panel from the Worker. Tolerant of the
  // route being unreachable (e.g. `vite` dev, offline) — it just shows an empty
  // state rather than erroring.
  async renderLeaderboard(s: SeasonState): Promise<void> {
    const host = document.getElementById("dailyLb");
    if (!host) return;
    const T = t();
    const shell = (inner: string): string =>
      `<div class="lbCard"><h3 class="lbTitle">${esc(T.lbTitle)}</h3>${inner}</div>`;
    host.innerHTML = shell(`<p class="lbNote">${esc(T.lbLoading)}</p>`);
    const data = await fetchLeaderboard(s.day, getClientId());
    // The view may have changed while the fetch was in flight.
    const st = this.deps.getState();
    if (st.phase !== "result" || st.season !== s) return;
    if (!data || data.entries.length === 0) {
      host.innerHTML = shell(`<p class="lbNote">${esc(T.lbEmpty)}</p>`);
      return;
    }
    const rows = data.entries
      .map(
        (e, i) =>
          `<tr class="${e.you ? "you" : ""}"><td class="pos">${i + 1}</td><td>${esc(e.name)}</td><td class="num">${e.teamPts}</td></tr>`,
      )
      .join("");
    // If the player ranked outside the returned slice, pin their own row below.
    const outside =
      data.youRank && data.youRank > data.entries.length && data.you
        ? `<tr class="you outside"><td class="pos">${data.youRank}</td><td>${esc(T.lbYou)}</td><td class="num">${data.you.teamPts}</td></tr>`
        : "";
    host.innerHTML = shell(
      `<table class="lbTable"><tr><th>${T.thPos}</th><th>${esc(T.lbTeam)}</th><th class="num">${T.thPts}</th></tr>${rows}${outside}</table>`,
    );
  }
}
